import { Injectable } from '@angular/core';
import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';

import Utils from '../../shared/utils';
import {
  FlockWeekData,
  InputMethodEnum,
  ValueRegistrationEnum,
  WeekKPI,
  WeekRegistrationKPIEnum,
  WeekRegistrationKPIItem,
} from '../models/flock.model';
import { WeekRegistrationKPIItemBenchmark } from '../models/flock.model';

@Injectable({
  providedIn: 'root',
})
export class FlockWeekregistrationKpiService {
  getKPIParameter(
    param: WeekRegistrationKPIEnum,
    dataSeries: DataSeries<number, number>,
  ): DataSeriesParameter {
    return dataSeries.metaData.parameters.find((c) => c.code === param);
  }

  getWeekRegistrationKPIItem(
    param: WeekRegistrationKPIEnum,
    showInOverview: boolean,
    benchmark: WeekRegistrationKPIItemBenchmark,
    flockWeekData: FlockWeekData | undefined,
    dataSeries: DataSeries<number, number>,
  ): WeekRegistrationKPIItem {
    const kpiParam = this.getKPIParameter(param, dataSeries);
    const item: WeekRegistrationKPIItem = {
      name: param,
      unit: kpiParam?.unit,
      value: flockWeekData?.calculations?.[param]?.value ?? undefined,
      decimalsPrecision: kpiParam?.numberOfDecimals,
      showInOverview,
      benchmark,
    };

    return item;
  }

  getValueRegistrationRanges(
    param: ValueRegistrationEnum,
    inputMethod: InputMethodEnum,
    amountOfHens: number,
  ): [number, number] {
    if (inputMethod === InputMethodEnum.PerDay) {
      switch (param) {
        case ValueRegistrationEnum.EggWeight:
          return [30, 80];
        case ValueRegistrationEnum.EggsAmount:
          return [0, Math.round(1.1 * amountOfHens)];
        case ValueRegistrationEnum.EggsAmountSecondKind:
          return [0, Math.round(1.1 * amountOfHens)];
        case ValueRegistrationEnum.Forage:
          return [0, Math.round(amountOfHens * (150.0 / 1000.0))];
        case ValueRegistrationEnum.SecondForage:
          return [0, Math.round(amountOfHens * (150.0 / 1000.0))];
        case ValueRegistrationEnum.HenWeight:
          return [1000, 3000];
        case ValueRegistrationEnum.Mortality:
          return [0, amountOfHens];
        case ValueRegistrationEnum.Water:
          return [0, Math.round(amountOfHens * (300.0 / 1000.0))];
      }
    } else {
      switch (param) {
        case ValueRegistrationEnum.EggWeight:
          return [30, 80];
        case ValueRegistrationEnum.EggsAmount:
          return [0, Math.round(1.1 * 7 * amountOfHens)];
        case ValueRegistrationEnum.EggsAmountSecondKind:
          return [0, Math.round(1.1 * 7 * amountOfHens)];
        case ValueRegistrationEnum.Forage:
          return [0, Math.round(7 * amountOfHens * (150.0 / 1000.0))];
        case ValueRegistrationEnum.SecondForage:
          return [0, Math.round(7 * amountOfHens * (150.0 / 1000.0))];
        case ValueRegistrationEnum.HenWeight:
          return [1000, 3000];
        case ValueRegistrationEnum.Mortality:
          return [0, amountOfHens];
        case ValueRegistrationEnum.Water:
          return [0, Math.round(7 * amountOfHens * (300.0 / 1000.0))];
      }
    }

    return undefined;
  }

  getWeekKPI(flockWeekData: FlockWeekData, dataSeries: DataSeries<number, number>): WeekKPI {
    // Add KPI's to weekKPI
    const weekKPI: WeekKPI = {
      week: flockWeekData.week,
      weekStart: flockWeekData.weekStartDate,
      RateOfLay: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.RateOfLay,
        true,
        { benchmarkCode: 'RateOfLay' },
        flockWeekData,
        dataSeries,
      ),
      AverageEggWeight: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.AverageEggWeight,
        true,
        { benchmarkCode: 'AverageEggWeight' },
        flockWeekData,
        dataSeries,
      ),
      TotalEggWeight: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.TotalEggWeight,
        false,
        { referenceKPI: 'TotalEggWeight' },
        flockWeekData,
        dataSeries,
      ),
      AverageEggWeightPerLayerPerDay: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.AverageEggWeightPerLayerPerDay,
        true,
        { referenceKPI: 'AverageEggWeightPerLayerPerDay' },
        flockWeekData,
        dataSeries,
      ),
      FeedConsumptionPerLayerInProduction: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumptionPerLayerInProduction,
        true,
        { referenceKPI: 'FeedConsumptionPerLayerInProduction' },
        flockWeekData,
        dataSeries,
      ),
      FeedConsumptionPerLayerInProductionCumulative: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumptionPerLayerInProductionCumulative,
        false,
        { benchmarkCode: 'FeedConsumptionPerLayerInProductionCumulative' },
        flockWeekData,
        dataSeries,
      ),
      FeedConsumptionPerLayerPerDay: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumptionPerLayerPerDay,
        true,
        { benchmarkCode: 'FeedConsumptionPerLayerPerDay' },
        flockWeekData,
        dataSeries,
      ),
      FeedConsumptionPerLayerPerDayCumulative: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumptionPerLayerPerDayCumulative,
        false,
        { benchmarkCode: 'FeedConsumptionPerLayerPerDayCumulative' },
        flockWeekData,
        dataSeries,
      ),
      EggCountPerLayerInProduction: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.EggCountPerLayerInProduction,
        false,
        { benchmarkCode: 'EggCountPerLayerInProduction' },
        flockWeekData,
        dataSeries,
      ),
      EggCountPerLayerInProductionCumulative: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.EggCountPerLayerInProductionCumulative,
        false,
        { benchmarkCode: 'EggCountPerLayerInProductionCumulative' },
        flockWeekData,
        dataSeries,
      ),
      FeedToEggRatio: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedToEggRatio,
        true,
        { benchmarkCode: 'FeedToEggRatio' },
        flockWeekData,
        dataSeries,
      ),
      FeedConversionRate: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConversionRate,
        true,
        { referenceKPI: 'FeedConversionRate' },
        flockWeekData,
        dataSeries,
      ),
      FeedConversionRateCumulative: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConversionRateCumulative,
        false,
        { benchmarkCode: 'FeedConversionRateCumulative' },
        flockWeekData,
        dataSeries,
      ),
      WaterToFeedRatio: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.WaterToFeedRatio,
        true,
        { benchmarkCode: 'WaterToFeedRatio' },
        flockWeekData,
        dataSeries,
      ),
      MortalityRate: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.MortalityRate,
        false,
        { referenceKPI: 'MortalityRate' },
        flockWeekData,
        dataSeries,
      ),
      MortalityRateCumulative: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.MortalityRateCumulative,
        true,
        { benchmarkCode: 'MortalityRateCumulative' },
        flockWeekData,
        dataSeries,
      ),
      TotalEggWeightPerLayerInProductionCumulative: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.TotalEggWeightPerLayerInProductionCumulative,
        false,
        { benchmarkCode: 'TotalEggWeightPerLayerInProductionCumulative' },
        flockWeekData,
        dataSeries,
      ),
      AverageLayerWeight: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.AverageLayerWeight,
        true,
        { benchmarkCode: 'AverageLayerWeight' },
        flockWeekData,
        dataSeries,
      ),
    };

    return weekKPI;
  }

  getAllWeekKpi(flockWeekData: FlockWeekData, dataSeries: DataSeries<number, number>): WeekKPI {
    const weekKPI = {
      ...this.getWeekKPI(flockWeekData, dataSeries),
      MortalityCount: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.MortalityCount,
        false,
        { referenceKPI: 'MortalityCount' },
        flockWeekData,
        dataSeries,
      ),
      TotalEggCount: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.TotalEggCount,
        false,
        { referenceKPI: 'TotalEggCount' },
        flockWeekData,
        dataSeries,
      ),
      TotalEggCountSecondGrade: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.TotalEggCountSecondGrade,
        false,
        { referenceKPI: 'TotalEggCountSecondGrade' },
        flockWeekData,
        dataSeries,
      ),
      FeedConsumption: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumption,
        false,
        { referenceKPI: 'FeedConsumption' },
        flockWeekData,
        dataSeries,
      ),
      SecondFeedConsumption: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.SecondFeedConsumption,
        false,
        { referenceKPI: 'SecondFeedConsumption' },
        flockWeekData,
        dataSeries,
      ),
      WaterConsumption: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.WaterConsumption,
        false,
        { referenceKPI: 'WaterConsumption' },
        flockWeekData,
        dataSeries,
      ),
    };

    return weekKPI;
  }

  getAveragesWeekKPI(
    flockWeekData: FlockWeekData[],
    dataSeries: DataSeries<number, number>,
  ): WeekKPI {
    if (flockWeekData.length === 0) {
      return {};
    }

    const lastWeek = Math.max(...flockWeekData.map((wr) => wr.week));
    const lastWeekData = flockWeekData.find((c) => c.week === lastWeek);

    return {
      RateOfLay: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.RateOfLayCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),

      AverageEggWeight: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.AverageEggWeightCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),

      AverageEggWeightPerLayerPerDay: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.AverageEggWeightPerLayerPerDayCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),

      FeedConsumptionPerLayerPerDay: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumptionPerLayerPerDayCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),

      FeedConsumptionPerLayerInProduction: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedConsumptionPerLayerInProductionCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),

      WaterToFeedRatio: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.WaterToFeedRatioCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),

      FeedToEggRatio: this.getWeekRegistrationKPIItem(
        WeekRegistrationKPIEnum.FeedToEggRatioCumulative,
        false,
        undefined,
        lastWeekData,
        dataSeries,
      ),
    };
  }

  getDateByHenAgeInDays(chickBirthDate: Date, ageInDays: number): Date {
    let date = Utils.toUtcDate(new Date(chickBirthDate), true);
    date.setDate(date.getDate() + ageInDays);
    date = Utils.toUtcDate(date, true);
    return date;
  }
}
